<template>
  <div class="page-container">
    <div class="page-header">
      <h2>股东唯一性列表</h2>
      <p>按股东唯一性展示；列表按最新持股数降序。含最新批次筹码：FIFO 成本、浮动盈亏、现金流盈亏（无推演数据时为空）。</p>
    </div>

    <div class="filter-section">
      <el-form inline :model="filterForm" class="filter-form">
        <el-form-item label="搜索">
          <HolderSuggestInput
            v-model="filterForm.keyword"
            type="raw"
            placeholder="股东名称/证券账户，支持汉字、拼音、首字母"
            style="width: 280px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadList">
            <el-icon><Search /></el-icon>
            查询
          </el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-section">
      <el-table v-loading="loading" :data="tableData" border stripe style="width: 100%">
        <el-table-column prop="holder_name" label="股东名称" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">
            <button
              type="button"
              class="su-link-button"
              @click="openHolderDetail(row.holder_name, row.securities_account_no || row.account_code || row.raw_shareholder_id)"
            >
              {{ row.holder_name || '—' }}
            </button>
          </template>
        </el-table-column>
        <el-table-column prop="securities_account_no" label="证券账户" min-width="120" show-overflow-tooltip />
        <el-table-column prop="contact_address" label="通讯地址" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">{{ row.contact_address || '—' }}</template>
        </el-table-column>
        <el-table-column prop="contact_phone" label="联系电话" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">{{ row.contact_phone || '—' }}</template>
        </el-table-column>
        <el-table-column prop="id_card_no" label="证件号码" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">{{ row.id_card_no || '—' }}</template>
        </el-table-column>
        <el-table-column prop="first_appear_time" label="首次出现时间" width="120" />
        <el-table-column prop="last_appear_time" label="最近出现时间" width="120" />
        <el-table-column prop="hold_period_count" label="持仓期数" width="100" align="right" />
        <el-table-column prop="latest_hold_vol" label="最新持股数" width="120" align="right">
          <template #default="{ row }">{{ formatNumber(row.latest_hold_vol) }}</template>
        </el-table-column>
        <el-table-column prop="latest_hold_ratio" label="最新持股比例" width="110" align="right">
          <template #default="{ row }">{{ formatRatio(row.latest_hold_ratio) }}</template>
        </el-table-column>
        <el-table-column prop="missing_period_count" label="未出现期数" width="100" align="right" />
        <el-table-column prop="fifo_cost_avg" label="FIFO成本" width="100" align="right">
          <template #default="{ row }">{{ row.fifo_cost_avg != null ? Number(row.fifo_cost_avg).toFixed(2) : '—' }}</template>
        </el-table-column>
        <el-table-column prop="fifo_pnl" label="浮动盈亏" width="110" align="right">
          <template #default="{ row }">{{ formatNumber(row.fifo_pnl) }}</template>
        </el-table-column>
        <el-table-column prop="cash_yield" label="现金流盈亏" width="110" align="right">
          <template #default="{ row }">{{ formatNumber(row.cash_yield) }}</template>
        </el-table-column>
      </el-table>
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.page_size"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadList"
          @current-change="loadList"
        />
      </div>
    </div>

    <!-- 股东详情弹窗 -->
    <ShareholderDetailModal
      :key="(holderDetailName || '') + '|' + (holderDetailRawShareholderId || '')"
      v-model="holderDetailVisible"
      :holder-name="holderDetailName"
      :core-name="holderDetailCoreName"
      :link-type="holderDetailLinkType"
      :raw-shareholder-id="holderDetailRawShareholderId"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { Search } from '@element-plus/icons-vue'
import axios from 'axios'
import HolderSuggestInput from '@/components/HolderSuggestInput.vue'
import ShareholderDetailModal from '@/components/ShareholderDetailModal.vue'

const filterForm = reactive({ keyword: '' })
const tableData = ref([])
const loading = ref(false)
const pagination = reactive({ page: 1, page_size: 20, total: 0 })

// 股东详情浮窗状态
const holderDetailVisible = ref(false)
const holderDetailName = ref('')
const holderDetailCoreName = ref('')
const holderDetailLinkType = ref(null)
const holderDetailRawShareholderId = ref('')

async function fetchCoreRelation(name) {
  if (!name) return { coreName: '', linkType: null }
  try {
    const res = await axios.get('/api/core_relation/', {
      params: { raw_keyword: name },
    })
    if (res.data?.status === 'ok' && Array.isArray(res.data.data) && res.data.data.length) {
      const row = res.data.data[0]
      const isControl = row.status === 'chairman_confirmed' || row.status === 'secretary_confirmed' || row.status === 'active'
      return {
        coreName: row.core_name || '',
        linkType: isControl ? 'control' : null,
      }
    }
  } catch (e) {
    // 静默失败
  }
  return { coreName: '', linkType: null }
}

function openHolderDetail(name, rawShareholderId = '') {
  holderDetailName.value = name || ''
  holderDetailCoreName.value = ''
  holderDetailLinkType.value = null
  holderDetailRawShareholderId.value = rawShareholderId || ''
  holderDetailVisible.value = true
}

function loadList() {
  loading.value = true
  axios.get('/api/unique_shareholder/list/', {
    params: {
      page: pagination.page,
      page_size: pagination.page_size,
      keyword: filterForm.keyword || undefined,
    },
  })
    .then(res => {
      if (res.data.status === 'ok') {
        tableData.value = res.data.data || []
        pagination.total = res.data.total || 0
      }
    })
    .catch(() => {})
    .finally(() => { loading.value = false })
}

function handleReset() {
  filterForm.keyword = ''
  pagination.page = 1
  loadList()
}

function formatNumber(n) {
  if (n === null || n === undefined) return '—'
  return new Intl.NumberFormat('zh-CN').format(n)
}

function formatRatio(r) {
  if (r === null || r === undefined) return '—'
  const n = Number(r)
  if (Number.isNaN(n)) return '—'
  if (n > 0 && n < 1) return (n * 100).toFixed(4) + '%'
  if (n >= 1 && n <= 100) return n.toFixed(4) + '%'
  return (n * 100).toFixed(4) + '%'
}

onMounted(() => loadList())
</script>

<style scoped>
.page-container { padding: 20px; }
.page-header { margin-bottom: 20px; }
.page-header h2 { margin: 0 0 8px 0; color: #303133; }
.page-header p { margin: 0; color: #606266; }
.filter-section {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}
.table-section {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}
.pagination-container { margin-top: 20px; display: flex; justify-content: flex-end; }

.su-link-button {
  border: none;
  padding: 0;
  margin: 0;
  background: none;
  color: #2563eb;
  cursor: pointer;
}

.su-link-button:hover {
  text-decoration: underline;
}
</style>
